import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import type { ContinuityFindingView, ContinuitySeverity } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useModelPicker } from "../ai/ModelPicker.js";

const SEV_LABEL: Record<ContinuitySeverity, string> = { high: "High", medium: "Medium", low: "Low" };
const TYPE_LABEL: Record<string, string> = {
  relationship: "Relationship",
  character: "Character",
  timeline: "Timeline",
  location: "Location",
  plot: "Plot",
  knowledge: "Knowledge",
  object: "Object",
  other: "Other"
};

interface Props {
  projectId: string;
  onOpenScene: (sceneId: string) => void;
}

/**
 * Continuity Intelligence card (lives in Insights). Runs a whole-book scan that
 * cross-checks the manuscript against characters, the relationship timeline,
 * locations, timeline and plot threads, and lists contradictions as warnings.
 * Findings are warnings only — nothing is ever auto-edited.
 */
export function ContinuityCard({ projectId, onOpenScene }: Props) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const [findings, setFindings] = useState<ContinuityFindingView[] | null>(null);
  const [scanning, setScanning] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);

  const load = useCallback(async () => {
    try {
      setFindings((await api.continuity(projectId)).findings);
    } catch {
      setFindings([]);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const scan = async () => {
    const choice = await pickModel({ task: "continuity" });
    if (!choice) return;
    setScanning(true);
    try {
      const res = await api.scanContinuity(projectId, choice);
      setFindings(res.findings);
      const open = res.findings.filter((f) => f.status === "open").length;
      toast.success(open > 0 ? `Scribe flagged ${open} possible issue${open === 1 ? "" : "s"}.` : "No continuity issues found.");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const setStatus = async (id: string, status: "open" | "dismissed") => {
    try {
      setFindings((status === "dismissed" ? await api.dismissContinuity(id) : await api.reopenContinuity(id)).findings);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Update failed");
    }
  };

  const open = useMemo(() => (findings ?? []).filter((f) => f.status === "open"), [findings]);
  const dismissed = useMemo(() => (findings ?? []).filter((f) => f.status === "dismissed"), [findings]);

  return (
    <section className="card insight-card insight-card-wide">
      <div className="insight-card-head">
        <Icon name="alert" size={16} style={{ color: "var(--warning, #d29922)" }} />
        <h3>Continuity</h3>
        {open.length > 0 && <span className="chip">{open.length}</span>}
        <div className="row gap-2" style={{ marginLeft: "auto" }}>
          <button className="btn btn-sm" onClick={scan} disabled={scanning}>
            <Icon name={scanning ? "clock" : "sparkles"} size={14} className={scanning ? "spin" : ""} />
            {scanning ? "Scanning…" : "Scan for issues"}
          </button>
        </div>
      </div>

      <p className="faint" style={{ margin: "0 0 12px", fontSize: 12 }}>
        Cross-checks characters, relationships, timeline, locations and plot threads for contradictions. Warnings only — nothing is changed.
      </p>

      {findings === null ? (
        <div className="muted">Loading…</div>
      ) : open.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          {dismissed.length > 0 ? "No open issues. " : "No issues flagged yet. "}
          Run a scan to check the manuscript.
        </p>
      ) : (
        <ul className="cont-list">
          {open.map((f) => (
            <ContinuityRow key={f.id} f={f} onOpenScene={onOpenScene} onDismiss={() => setStatus(f.id, "dismissed")} />
          ))}
        </ul>
      )}

      {dismissed.length > 0 && (
        <div className="cont-dismissed">
          <button className="btn btn-ghost btn-sm" onClick={() => setShowDismissed((v) => !v)}>
            <Icon name={showDismissed ? "chevronDown" : "chevronRight"} size={13} />
            {dismissed.length} dismissed
          </button>
          {showDismissed && (
            <ul className="cont-list">
              {dismissed.map((f) => (
                <ContinuityRow key={f.id} f={f} onOpenScene={onOpenScene} onReopen={() => setStatus(f.id, "open")} dimmed />
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function ContinuityRow({
  f,
  onOpenScene,
  onDismiss,
  onReopen,
  dimmed
}: {
  f: ContinuityFindingView;
  onOpenScene: (sceneId: string) => void;
  onDismiss?: () => void;
  onReopen?: () => void;
  dimmed?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className={`cont-item ${dimmed ? "is-dismissed" : ""}`}>
      <span className={`cont-sev cont-sev-${f.severity}`} title={`${SEV_LABEL[f.severity]} severity`} />
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="cont-line">
          <span className={`cont-level cont-level-${f.level}`}>{f.level}</span>
          <span className="cont-type">{TYPE_LABEL[f.type] ?? f.type}</span>
          <span className="cont-summary">{f.summary}</span>
        </div>
        {(f.detail || f.sceneTitle || f.conflictSceneTitle) && (
          <div className="faint cont-meta">
            {f.sceneTitle && (
              <button className="cont-anchor" onClick={() => f.sceneId && onOpenScene(f.sceneId)} disabled={!f.sceneId}>
                {f.chapterTitle} · {f.sceneTitle}
              </button>
            )}
            {f.conflictSceneTitle && (
              <>
                <span className="cont-vs">vs</span>
                <button className="cont-anchor" onClick={() => f.conflictSceneId && onOpenScene(f.conflictSceneId)} disabled={!f.conflictSceneId}>
                  {f.conflictChapterTitle} · {f.conflictSceneTitle}
                </button>
              </>
            )}
            {f.confidence && <span className="cont-conf">{f.confidence} confidence</span>}
            {f.detail && (
              <button className="cont-expand" onClick={() => setExpanded((v) => !v)}>
                {expanded ? "Hide" : "Details"}
              </button>
            )}
          </div>
        )}
        {expanded && f.detail && <p className="cont-detail">{f.detail}{f.evidence ? ` — ${f.evidence}` : ""}</p>}
      </div>
      {onDismiss && (
        <button className="btn btn-icon btn-ghost btn-sm" title="Dismiss (not an issue)" onClick={onDismiss}>
          <Icon name="check" size={14} />
        </button>
      )}
      {onReopen && (
        <button className="btn btn-icon btn-ghost btn-sm" title="Reopen" onClick={onReopen}>
          <Icon name="undo" size={14} />
        </button>
      )}
    </li>
  );
}
